import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  Grid,
  useTheme,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Divider
} from "@mui/material";
import {
  Search,
  Filter,
  Hash
} from 'lucide-react';
import AddOnCard from "./AddOnCard";
import Constants from "../../utils/constants/Constants";
import { tokens } from "../../theme";

const AddonsList = ({
  categoryFilters,
  onCategoryFilterChange,
  searchQuery,
  onSearchQueryChange,
  onItemQuantityChange,
  requestedAddOns,
  addOnsData,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const categories = useMemo(() => {
    const seenCategories = new Set();
    return addOnsData
      .filter((item) => (item.addonType ?? Constants.ADDON_TYPE.CUSTOMER) === Constants.ADDON_TYPE.CUSTOMER)
      .reduce((acc, item) => {
        if (!seenCategories.has(item.category)) {
          acc.push({
            value: item.category,
            label: item.category.charAt(0).toUpperCase() + item.category.slice(1)
          });
          seenCategories.add(item.category);
        }
        return acc;
      }, []);
  }, [addOnsData]);

  const filteredItems = useMemo(() => {
    return addOnsData.filter((item) => {
      const isActive = item.active ?? item.isActive ?? false;
      const isCustomerAddOn = (item.addonType ?? Constants.ADDON_TYPE.CUSTOMER) === Constants.ADDON_TYPE.CUSTOMER;
      if (!isCustomerAddOn) return false;
      if (!isActive) return false;
      if (categoryFilters.length > 0 && !categoryFilters.includes(item.category)) {
        return false;
      }
      if (searchQuery && !item.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [addOnsData, categoryFilters, searchQuery]);

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3} alignItems="center" sx={{ mb: 4 }}>
        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            placeholder="Search premium add-ons..."
            value={searchQuery}
            onChange={onSearchQueryChange}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} color={colors.grey[400]} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={7}>
          <Box display="flex" alignItems="center" gap={2}>
            <Filter size={18} color={colors.greenAccent[500]} />
            <Box sx={{ overflowX: 'auto', pb: 1, '&::-webkit-scrollbar': { display: 'none' } }}>
              <ToggleButtonGroup
                value={categoryFilters}
                onChange={(e, val) => {
                  // Reconcile with parent's single-item toggle logic
                  const clickedOption = e.currentTarget.value;
                  onCategoryFilterChange(clickedOption);
                }}
                aria-label="category filters"
                sx={{
                  display: 'flex',
                  gap: 1,
                  border: 'none',
                  '& .MuiToggleButtonGroup-grouped': {
                    border: `1px solid ${colors.grey[500]}4D`,
                    borderRadius: '10px !important',
                    px: 2,
                    py: 0.5,
                    textTransform: 'none',
                    fontWeight: '600',
                    '&.Mui-selected': {
                      bgcolor: colors.greenAccent[500],
                      color: colors.primary[500],
                      '&:hover': { bgcolor: colors.greenAccent[400] }
                    }
                  }
                }}
              >
                {categories.map((cat) => (
                  <ToggleButton key={cat.value} value={cat.value}>
                    {cat.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ mb: 4, opacity: 0.3 }} />

      <Grid container spacing={3}>
        {filteredItems.map((item) => (
          <Grid item key={item.id} xs={12} sm={6} lg={4}>
            <AddOnCard addOn={item} />
            <Box sx={{ mt: 2, px: 1 }}>
              <TextField
                fullWidth
                label="Provision Quantity"
                type="number"
                size="small"
                inputProps={{ min: 0 }}
                defaultValue={
                  requestedAddOns.find(
                    (req) => req.addOn.name === item.name
                  )?.quantity || 0
                }
                onChange={(e) => onItemQuantityChange(item, e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "10px" }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Hash size={14} color={colors.grey[400]} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>
        ))}
        {filteredItems.length === 0 && (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: '20px' }}>
              <Typography variant="h6" color={colors.grey[400]}>No matching add-ons found.</Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

AddonsList.propTypes = {
  categoryFilters: PropTypes.array.isRequired,
  onCategoryFilterChange: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  onSearchQueryChange: PropTypes.func.isRequired,
  onItemQuantityChange: PropTypes.func.isRequired,
  requestedAddOns: PropTypes.array.isRequired,
  addOnsData: PropTypes.array.isRequired,
};

export default AddonsList;
